import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import logo from '../assets/Grey Imports Logo.png';
import { supabase } from '../context/AuthContext';
import AdminLayout from './AdminLayout';

const PAGE_SIZE = 50;

const ORDERABLE_COLUMNS = new Set(['sent_at', 'mr_approval']);

const SORT_OPTIONS = [
    { value: 'sent_at:asc', label: 'Oldest first' },
    { value: 'sent_at:desc', label: 'Newest first' },
    { value: 'mr_approval:asc', label: 'MRE No (asc)' },
    { value: 'mr_approval:desc', label: 'MRE No (desc)' },
];

function getMreSortKey(row) {
    const approval = row.modelReport?.mr_approval
        ?? row.customer_model_reports?.mr_approval
        ?? row.email_content?.preview?.mr_approval;
    return parseInt(approval?.replace(/^MRE-/i, '') || '0', 10);
}

function sortRowsByMre(rows, ascending) {
    return [...rows].sort((a, b) => {
        const diff = getMreSortKey(a) - getMreSortKey(b);
        if (diff !== 0) return ascending ? diff : -diff;
        return new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime();
    });
}

async function attachModelReports(notifications) {
    if (!notifications?.length) return [];

    const missingMrIds = [...new Set(
        notifications
            .filter((n) => !n.customer_model_reports && n.mr_id)
            .map((n) => n.mr_id)
    )];

    let reportById = {};
    if (missingMrIds.length) {
        const { data: reports, error } = await supabase
            .from('customer_model_reports')
            .select('id, make, model, mr_approval, mr_wi_reference, mr_vc_reference')
            .in('id', missingMrIds);

        if (error) {
            console.warn('Could not load linked model reports:', error.message);
        } else {
            reportById = Object.fromEntries((reports || []).map((r) => [r.id, r]));
        }
    }

    return notifications.map((row) => ({
        ...row,
        modelReport: row.customer_model_reports ?? reportById[row.mr_id] ?? null,
    }));
}

async function findMatchingReportIds(term) {
    const pattern = `%${term}%`;
    const { data, error } = await supabase
        .from('customer_model_reports')
        .select('id')
        .or(`mr_approval.ilike.${pattern},make.ilike.${pattern},model.ilike.${pattern}`);

    if (error) {
        console.warn('Model report search failed:', error.message);
        return [];
    }
    return (data || []).map((r) => r.id);
}

function extractHtml(emailContent) {
    if (!emailContent) return null;
    if (typeof emailContent === 'string') return emailContent;
    if (emailContent.message) return emailContent.message;
    for (const key of ['html', 'body', 'content', 'html_body', 'email_html']) {
        if (emailContent[key]) return emailContent[key];
    }
    return JSON.stringify(emailContent, null, 2);
}

function injectResponsiveEmailStyles(html) {
    if (!html || !html.trim().startsWith('<')) return html;

    const responsiveStyles = `
<style data-variation-history-mobile>
@media (max-width: 475px) {
    html, body {
        font-size: 12px !important;
        line-height: 1.4 !important;
        margin: 0 !important;
        padding-bottom: 30px !important;
    }

    body, p, span, div, td, th, li, a, strong, em {
        font-size: 12px !important;
        line-height: 1.4 !important;
    }

    img {
        max-width: 96px !important;
        height: auto !important;
    }

    h1 { font-size: 18px !important; }
    h2 { font-size: 16px !important; }
    h3 { font-size: 14px !important; }
    h4, h5, h6 { font-size: 13px !important; }

    table {
        width: 100% !important;
        table-layout: fixed !important;
    }

    th, td {
        padding: 6px 8px !important;
        word-break: break-word !important;
    }

    th {
        width: 34% !important;
        white-space: normal !important;
    }

    td {
        width: 66% !important;
    }
}
</style>`;

    if (html.includes('</head>')) {
        return html.replace('</head>', `${responsiveStyles}</head>`);
    }

    if (html.includes('<body')) {
        return html.replace(/<body([^>]*)>/i, `<body$1>${responsiveStyles}`);
    }

    return `${responsiveStyles}${html}`;
}

function getMrApprovalLabel(row) {
    const report = row.modelReport;
    if (report?.mr_approval) return report.mr_approval;
    const preview = row.email_content?.preview;
    if (preview?.mr_approval) return preview.mr_approval;
    return row.mr_id != null ? `#${row.mr_id}` : '—';
}

function getVehicleLabel(row) {
    const report = row.modelReport;
    if (report?.make || report?.model) {
        return [report.make, report.model].filter(Boolean).join(' ');
    }
    const preview = row.email_content?.preview;
    if (preview?.make || preview?.model) {
        return [preview.make, preview.model].filter(Boolean).join(' ');
    }
    return null;
}

function ExpandableText({ text, maxLines = 2 }) {
    const [expanded, setExpanded] = useState(false);
    const [canExpand, setCanExpand] = useState(false);
    const textRef = useRef(null);

    useLayoutEffect(() => {
        const el = textRef.current;
        if (!el || expanded) return;
        setCanExpand(el.scrollHeight > el.clientHeight + 1);
    }, [text, expanded, maxLines]);

    if (!text) {
        return <span className="text-gray-300">—</span>;
    }

    return (
        <div className="min-w-[12rem] max-w-lg max-[475px]:min-w-0 max-[475px]:max-w-full">
            <p
                ref={textRef}
                className="whitespace-pre-wrap break-words max-[475px]:text-[12px] max-[475px]:leading-5"
                style={expanded ? undefined : {
                    display: '-webkit-box',
                    WebkitLineClamp: maxLines,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                }}
            >
                {text}
            </p>
            {(canExpand || expanded) && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setExpanded((open) => !open);
                    }}
                    className="mt-1 text-xs font-medium text-blue-600 hover:text-blue-700 focus:outline-none focus:underline max-[475px]:text-[11px]"
                >
                    {expanded ? 'View less' : 'View more'}
                </button>
            )}
        </div>
    );
}

export default function VariationHistory() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const mrIdParam = searchParams.get('mr_id');
    const filterMrId = mrIdParam && !Number.isNaN(Number(mrIdParam)) ? Number(mrIdParam) : null;

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [sortCol, setSortCol] = useState('sent_at');
    const [sortDir, setSortDir] = useState('desc');
    const [page, setPage] = useState(0);
    const [refreshCounter, setRefreshCounter] = useState(0);
    const [selected, setSelected] = useState(null);
    const [expandedDetails, setExpandedDetails] = useState({});

    useEffect(() => {
        setPage(0);
    }, [filterMrId]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        let cancelled = false;

        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                const term = debouncedSearch.trim();
                const sortByMre = sortCol === 'mr_approval';
                const ascending = sortDir === 'asc';

                let query = supabase
                    .from('mr_variation_notifications')
                    .select('*', { count: 'exact' });

                if (!sortByMre) {
                    const orderColumn = ORDERABLE_COLUMNS.has(sortCol) ? sortCol : 'sent_at';
                    query = query.order(orderColumn, { ascending });
                }

                if (filterMrId) {
                    query = query.eq('mr_id', filterMrId);
                }

                if (term) {
                    const pattern = `%${term}%`;
                    const numericId = parseInt(term, 10);
                    const matchingReportIds = await findMatchingReportIds(term);
                    if (cancelled) return;

                    const orParts = [
                        `mr_wi_reference.ilike.${pattern}`,
                        `mr_vc_reference.ilike.${pattern}`,
                        `variation_description.ilike.${pattern}`,
                    ];
                    if (!isNaN(numericId)) {
                        orParts.push(`mr_id.eq.${numericId}`);
                    }
                    if (matchingReportIds.length > 0) {
                        orParts.push(`mr_id.in.(${matchingReportIds.join(',')})`);
                    }
                    query = query.or(orParts.join(','));
                }

                if (!sortByMre) {
                    query = query.range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);
                }

                const { data, error: err, count } = await query;
                if (cancelled) return;
                if (err) throw err;

                const enriched = await attachModelReports(data || []);
                if (cancelled) return;

                const totalCount = count ?? enriched.length;

                if (sortByMre) {
                    const sorted = sortRowsByMre(enriched, ascending);
                    const start = page * PAGE_SIZE;
                    setRows(sorted.slice(start, start + PAGE_SIZE));
                    setTotal(totalCount);
                } else {
                    setRows(enriched);
                    setTotal(totalCount);
                }
            } catch (e) {
                if (cancelled) return;
                console.error('Variation history fetch failed:', e);
                setError(e.message || 'Failed to load variation notifications');
                setRows([]);
                setTotal(0);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchData();
        return () => { cancelled = true; };
    }, [debouncedSearch, sortCol, sortDir, page, filterMrId, refreshCounter]);

    const openModelReport = (mrId) => {
        const returnPath = filterMrId
            ? `/admin/variation-history?mr_id=${filterMrId}`
            : '/admin/variation-history';
        navigate(`/admin/model-reports/${mrId}`, { state: { from: returnPath } });
    };

    const toggleDetails = (rowId) => {
        setExpandedDetails((current) => ({
            ...current,
            [rowId]: !current[rowId],
        }));
    };

    const totalPages = Math.ceil(total / PAGE_SIZE);
    const indexOfFirst = page * PAGE_SIZE + 1;
    const indexOfLast = Math.min(page * PAGE_SIZE + PAGE_SIZE, total);

    const htmlContent = selected ? extractHtml(selected.email_content) : null;
    const isHtml = htmlContent && htmlContent.trim().startsWith('<');
    const previewContent = isHtml ? injectResponsiveEmailStyles(htmlContent) : htmlContent;

    const PaginationControls = () => {
        const maxVisible = 5;
        let startP = Math.max(0, page - Math.floor(maxVisible / 2));
        let endP = Math.min(totalPages - 1, startP + maxVisible - 1);
        if (endP - startP + 1 < maxVisible) startP = Math.max(0, endP - maxVisible + 1);
        const pageNums = Array.from({ length: endP - startP + 1 }, (_, i) => startP + i);

        return (
            <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{total === 0 ? 0 : indexOfFirst}</span> to{' '}
                        <span className="font-medium">{indexOfLast}</span> of{' '}
                        <span className="font-medium">{total}</span> results
                    </p>
                    <nav className="inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                        <button
                            type="button"
                            onClick={() => setPage(p => Math.max(p - 1, 0))}
                            disabled={page === 0}
                            className="relative inline-flex items-center px-2 py-2 text-gray-400 rounded-l-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
                        >
                            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                        </button>
                        {startP > 0 && (
                            <>
                                <button type="button" onClick={() => setPage(0)} className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 bg-white hover:bg-gray-50">1</button>
                                {startP > 1 && <span className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 bg-white">...</span>}
                            </>
                        )}
                        {pageNums.map(n => (
                            <button
                                type="button"
                                key={n}
                                onClick={() => setPage(n)}
                                className={`relative inline-flex items-center px-4 py-2 text-sm font-medium border ${page === n
                                    ? 'z-10 bg-blue-100 border-blue-600 text-blue-700'
                                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                    }`}
                            >
                                {n + 1}
                            </button>
                        ))}
                        {endP < totalPages - 1 && (
                            <>
                                {endP < totalPages - 2 && <span className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 bg-white">...</span>}
                                <button type="button" onClick={() => setPage(totalPages - 1)} className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 bg-white hover:bg-gray-50">{totalPages}</button>
                            </>
                        )}
                        <button
                            type="button"
                            onClick={() => setPage(p => Math.min(p + 1, totalPages - 1))}
                            disabled={page >= totalPages - 1}
                            className="relative inline-flex items-center px-2 py-2 text-gray-400 rounded-r-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
                        >
                            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>
                        </button>
                    </nav>
                </div>
            </div>
        );
    };

    const columns = [
        { key: 'serial', label: 'S No.', className: 'max-sm:hidden' },
        { key: 'mre', label: 'MRE No' },
        { key: 'wi', label: 'WI Reference', className: 'max-xl:hidden' },
        { key: 'vc', label: 'VC Reference', className: 'max-xl:hidden' },
        { key: 'variation', label: 'Variation Description' },
        { key: 'sentAt', label: 'Sent At', className: 'max-xl:hidden' },
        { key: 'action', label: 'Action' },
    ];

    return (
        <AdminLayout
            activeItemId="variation-history"
            title="Variation History"
            subtitle="Access previously sent variation notifications for your reports."
            contentClassName="mx-auto max-w-7xl"
        >


            {selected && createPortal(
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/28 px-4 py-6 backdrop-blur-[3px]"
                    onClick={e => e.target === e.currentTarget && setSelected(null)}
                >
                    <div
                        className="flex max-h-full w-full max-w-4xl flex-col overflow-hidden rounded-[1rem] border border-slate-300 bg-white ring-1 ring-slate-200/80 shadow-[0_24px_64px_-28px_rgba(15,23,42,0.42)] max-[475px]:rounded-[0.85rem]"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Email preview"
                    >
                        <div className="flex items-start justify-between gap-4 px-5 pt-5 max-[475px]:gap-3 max-[475px]:px-3.5 max-[475px]:pt-3.5">
                            <div>
                                <div className="inline-flex items-center rounded-[0.55rem] bg-sky-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-sky-700 max-[475px]:text-[9px]">
                                    Email Preview
                                </div>
                                <h2 className="mt-2.5 text-md font-medium text-gray-900 uppercase tracking-[0.04em] max-[475px]:mt-2 max-[475px]:text-xs max-[475px]:leading-5">
                                    Email Sent for {getMrApprovalLabel(selected)} on {new Date(selected.sent_at).toLocaleString('en-AU', {
                                        day: '2-digit', month: 'short', year: 'numeric',
                                        hour: '2-digit', minute: '2-digit',
                                    })}
                                </h2>
                                {selected.email_content?.subject && (
                                    <p className="mt-2 text-sm text-slate-500 max-[475px]:text-xs max-[475px]:leading-5">
                                        Subject: {selected.email_content.subject}
                                    </p>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => setSelected(null)}
                                className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                            >
                                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </div>

                        <div className="px-5 pb-5 pt-4 max-[475px]:px-3 max-[475px]:pb-3 max-[475px]:pt-2.5">
                            <div className="overflow-hidden rounded-[1rem] border border-slate-200 bg-slate-50/70">
                                {isHtml ? (
                                    <iframe
                                        title={`email-preview-${selected.id}`}
                                        srcDoc={previewContent}
                                        sandbox="allow-same-origin"
                                        className="block w-full border-none bg-white"
                                        style={{ height: '70vh' }}
                                    />
                                ) : (
                                    <pre className="m-0 max-h-[70vh] overflow-y-auto bg-slate-50 p-6 text-xs font-mono leading-relaxed text-gray-700 whitespace-pre-wrap max-[475px]:p-3 max-[475px]:text-[11px] max-[475px]:leading-5">
                                        {previewContent}
                                    </pre>
                                )}
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            <div className="mb-6 rounded-[2rem] border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
                <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                    <div className="grid flex-1 gap-4 lg:grid-cols-2 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,0.8fr)_minmax(0,0.8fr)]">
                        <input
                            type="text"
                            value={search}
                            onChange={e => { setSearch(e.target.value); setPage(0); }}
                            placeholder="Search MRE, refs, description..."
                            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        />
                        <select
                            value={`${sortCol}:${sortDir}`}
                            onChange={e => {
                                const [c, d] = e.target.value.split(':');
                                setSortCol(c); setSortDir(d); setPage(0);
                            }}
                            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        >
                            {SORT_OPTIONS.map(({ value, label }) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </select>
                        <button
                            type="button"
                            onClick={() => setRefreshCounter(c => c + 1)}
                            className="inline-flex items-center justify-center rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-100"
                        >
                            ↻ Refresh
                        </button>
                    </div>
                </div>
            </div>

            <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
                <div className="overflow-x-auto shadow-sm rounded-lg">
                    <table className="min-w-full bg-white">
                        <thead className="bg-gray-50">
                            <tr>
                                {columns.map(({ key, label, className }) => (
                                    <th
                                        key={key}
                                        className={`py-4 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap max-[475px]:px-2.5 max-[475px]:py-3 max-[475px]:text-[10px] ${className || ''}`}
                                    >
                                        {label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan={columns.length} className="px-6 py-12 text-center text-sm text-gray-500">
                                        Loading...
                                    </td>
                                </tr>
                            ) : error ? (
                                <tr>
                                    <td colSpan={columns.length} className="px-6 py-12 text-center text-sm text-red-600">
                                        {error}
                                    </td>
                                </tr>
                            ) : rows.length === 0 ? (
                                <tr>
                                    <td colSpan={columns.length} className="px-6 py-12 text-center text-sm text-gray-500">
                                        No records found.
                                    </td>
                                </tr>
                            ) : rows.map((row, index) => (
                                <React.Fragment key={row.id}>
                                    <tr
                                        className="hover:bg-gray-50 transition-colors duration-150 xl:cursor-default max-xl:cursor-pointer"
                                        onClick={() => {
                                            if (window.innerWidth < 1280) {
                                                toggleDetails(row.id);
                                            }
                                        }}
                                    >
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 font-mono max-sm:hidden">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleDetails(row.id);
                                                    }}
                                                    aria-expanded={Boolean(expandedDetails[row.id])}
                                                    className="hidden h-8 w-8 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-100 sm:max-xl:flex"
                                                >
                                                    <svg
                                                        className={`h-4 w-4 transition-transform ${expandedDetails[row.id] ? 'rotate-180' : ''}`}
                                                        viewBox="0 0 20 20"
                                                        fill="currentColor"
                                                        aria-hidden="true"
                                                    >
                                                        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                                                    </svg>
                                                </button>
                                                <span>{page * PAGE_SIZE + index + 1}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium max-sm:text-[13px] max-[475px]:px-2.5 max-[475px]:py-3">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleDetails(row.id);
                                                    }}
                                                    aria-expanded={Boolean(expandedDetails[row.id])}
                                                    className="hidden h-8 w-8 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-100 max-sm:flex"
                                                >
                                                    <svg
                                                        className={`h-4 w-4 transition-transform ${expandedDetails[row.id] ? 'rotate-180' : ''}`}
                                                        viewBox="0 0 20 20"
                                                        fill="currentColor"
                                                        aria-hidden="true"
                                                    >
                                                        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                                                    </svg>
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openModelReport(row.mr_id);
                                                    }}
                                                    className="text-blue-600 hover:text-blue-700 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-1 rounded"
                                                >
                                                    {getMrApprovalLabel(row)}
                                                </button>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-900 break-words max-xl:hidden max-sm:text-[13px]">
                                            {row.mr_wi_reference || <span className="text-gray-300">—</span>}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-900 break-words max-xl:hidden max-sm:text-[13px]">
                                            {row.mr_vc_reference || <span className="text-gray-300">—</span>}
                                        </td>
                                        <td className="px-4 py-4 text-sm text-gray-900 max-sm:text-[13px] max-[475px]:px-2.5 max-[475px]:py-3 max-[475px]:w-full max-[475px]:max-w-0">
                                            <ExpandableText text={row.variation_description} maxLines={2} />
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 max-xl:hidden max-sm:text-[13px]">
                                            {new Date(row.sent_at).toLocaleString('en-AU', {
                                                day: '2-digit', month: 'short', year: 'numeric',
                                                hour: '2-digit', minute: '2-digit',
                                            })}
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 max-sm:text-[13px] max-[475px]:px-2.5 max-[475px]:py-3">
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelected(row);
                                                }}
                                                aria-label="View Email"
                                                className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 max-sm:justify-center max-sm:px-2.5 max-sm:py-1.5 max-sm:text-[13px]"
                                            >
                                                <svg className="h-4 w-4 sm:mr-2" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                                                </svg>
                                                <span className="max-sm:hidden">View Email</span>
                                            </button>
                                        </td>
                                    </tr>
                                    {expandedDetails[row.id] && (
                                        <tr className="xl:hidden bg-slate-50/70">
                                            <td colSpan={columns.length} className="px-4 pb-4 pt-0">
                                                <div className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white shadow-sm">
                                                    <div className="border-b border-slate-200">
                                                        <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50/80 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
                                                            <div className="flex items-center gap-3">
                                                                <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm">
                                                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5.586a1 1 0 0 1 .707.293l5.414 5.414a1 1 0 0 1 .293.707V19a2 2 0 0 1-2 2Z" />
                                                                    </svg>
                                                                </span>
                                                                <div>
                                                                    <p className="text-sm font-medium uppercase tracking-[0.04em] sm:text-md">Details</p>
                                                                    <h4 className="text-xs font-medium text-slate-500 sm:text-sm">Review the variation history details.</h4>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="px-5 py-4">
                                                            <div className="grid gap-3 sm:grid-cols-3">
                                                                <div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3">
                                                                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 sm:text-[11px]">WI Reference</p>
                                                                    <p className="mt-2 break-words text-xs font-normal text-slate-700 sm:text-sm">{row.mr_wi_reference || '—'}</p>
                                                                </div>
                                                                <div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3">
                                                                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 sm:text-[11px]">VC Reference</p>
                                                                    <p className="mt-2 break-words text-xs font-normal text-slate-700 sm:text-sm">{row.mr_vc_reference || '—'}</p>
                                                                </div>
                                                                <div className="min-w-0 rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-3">
                                                                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400 sm:text-[11px]">Sent At</p>
                                                                    <p className="mt-2 break-words text-xs font-normal text-slate-700 sm:text-sm">
                                                                        {new Date(row.sent_at).toLocaleString('en-AU', {
                                                                            day: '2-digit', month: 'short', year: 'numeric',
                                                                            hour: '2-digit', minute: '2-digit',
                                                                        })}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>

                <PaginationControls />
            </div>
        </AdminLayout>
    );
}
